import { useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Grid,
  InputAdornment,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  AdminPanelSettings as AdminIcon,
  Apps as AppsIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Security as SecurityIcon,
  Shield as ShieldIcon,
  Verified as VerifiedIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../state/AuthContext';
import { useToast } from '../state/ToastContext';
import { api } from '../lib/apiClient';
import CustomButton from '../components/CustomButton';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { getApiErrorMessage } from '../utils/errorUtils';

type PasswordStrength = 'weak' | 'medium' | 'strong';

function calcPasswordStrength(password: string): PasswordStrength {
  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[a-z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  if (score <= 2) return 'weak';
  if (score <= 4) return 'medium';
  return 'strong';
}

const strengthColors: Record<PasswordStrength, string> = {
  weak: '#e53935',
  medium: '#fb8c00',
  strong: '#43a047',
};

const strengthValues: Record<PasswordStrength, number> = {
  weak: 33,
  medium: 66,
  strong: 100,
};

function AppAvatar({ name, src }: { name: string; src?: string | null }) {
  return (
    <Avatar
      src={src ?? undefined}
      sx={{
        width: 48,
        height: 48,
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
        fontWeight: 700,
        fontSize: '1.125rem',
      }}
    >
      {name.charAt(0).toUpperCase()}
    </Avatar>
  );
}

function SectionIcon({ icon }: { icon: React.ReactNode }) {
  return (
    <Box
      sx={{
        p: 1,
        borderRadius: 1.5,
        bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        lineHeight: 0,
      }}
    >
      {icon}
    </Box>
  );
}

function StatCard({
  icon,
  value,
  label,
}: {
  icon: React.ReactNode;
  value: string | number;
  label: string;
}) {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: alpha(theme.palette.primary.main, 0.03),
        textAlign: 'center',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: 'primary.light',
          boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.1)}`,
        },
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: '50%',
          bgcolor: alpha(theme.palette.primary.main, 0.1),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'primary.main',
          lineHeight: 0,
        }}
      >
        {icon}
      </Box>
      <Typography variant="h5" fontWeight={700} color="primary.main">
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary" fontWeight={500}>
        {label}
      </Typography>
    </Paper>
  );
}

export default function ProfilePage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { showToast } = useToast();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(user?.mustChangePassword || false);

  const passwordStrength = newPassword ? calcPasswordStrength(newPassword) : null;
  const strengthLabel = passwordStrength
    ? t(`profile.security.passwordStrength.${passwordStrength}`)
    : '';
  const dateLocale = i18n.language.startsWith('pt') ? ptBR : undefined;
  const dateFormat = i18n.language.startsWith('pt') ? "dd 'de' MMMM 'de' yyyy" : 'MMMM do, yyyy';

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(false);

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError(t('profile.error.allFieldsRequired'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError(t('profile.error.passwordMismatch'));
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError(t('profile.error.passwordTooShort'));
      return;
    }
    if (currentPassword === newPassword) {
      setPasswordError(t('profile.error.samePassword'));
      return;
    }

    setIsChangingPassword(true);
    try {
      await api.changePassword(null, { currentPassword, newPassword });
      setPasswordSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      showToast(t('profile.success.passwordChanged'), 'success');
      setTimeout(() => {
        setShowPasswordForm(false);
        setPasswordSuccess(false);
      }, 2000);
    } catch (error) {
      setPasswordError(getApiErrorMessage(error, t, 'profile.error.changeFailed'));
    } finally {
      setIsChangingPassword(false);
    }
  };

  if (!user) return null;

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), dateFormat, { locale: dateLocale });
    } catch {
      return dateString;
    }
  };

  return (
    <Box role="main" aria-label={t('profile.title')}>
      {/* Hero */}
      <Paper
        elevation={0}
        role="banner"
        aria-label={t('profile.accountInfo.title')}
        sx={{
          background: `linear-gradient(135deg, ${isDark ? theme.palette.primary.dark : theme.palette.primary.main} 0%, ${theme.palette.grey[900]} 100%)`, // Gradiente escuro de alto contraste
          borderRadius: 3,
          p: { xs: 3, sm: 4 },
          mb: 4,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            right: 0,
            width: '40%',
            height: '100%',
            background: `radial-gradient(circle at top right, ${alpha(theme.palette.common.white, 0.1)} 0%, transparent 70%)`,
          },
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 2, sm: 4 }}
          alignItems={{ xs: 'center', sm: 'flex-start' }}
        >
          <Avatar
            sx={{
              width: { xs: 96, sm: 120 },
              height: { xs: 96, sm: 120 },
              bgcolor: 'common.white',
              color: 'primary.main',
              fontSize: { xs: '2.5rem', sm: '3rem' },
              fontWeight: 700,
              border: `4px solid ${alpha(theme.palette.common.white, 0.3)}`,
              boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.2)}`,
            }}
          >
            {user.displayName?.charAt(0).toUpperCase() || user.email?.charAt(0).toUpperCase()}
          </Avatar>

          <Stack spacing={1.5} sx={{ flex: 1, textAlign: { xs: 'center', sm: 'left' } }}>
            <Box>
              <Typography variant="h4" fontWeight={700} sx={{ color: 'common.white' }}>
                {user.displayName}
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{ color: alpha(theme.palette.common.white, 0.85) }}
              >
                @{user.username}
              </Typography>
            </Box>

            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              justifyContent={{ xs: 'center', sm: 'flex-start' }}
            >
              <Chip
                label={t(`roles.${user.role}`)}
                sx={{
                  bgcolor: alpha(theme.palette.common.white, 0.12),
                  color: 'common.white',
                  fontWeight: 600,
                  backdropFilter: 'blur(10px)',
                  border: `1px solid ${alpha(theme.palette.common.white, 0.25)}`,
                }}
              />
              {user.isMaster && (
                <Chip
                  icon={<AdminIcon />}
                  label={t('profile.accountInfo.masterBadge')}
                  sx={{
                    bgcolor: 'rgba(251, 191, 36, 0.15)',
                    color: '#fde047',
                    fontWeight: 600,
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(251, 191, 36, 0.4)',
                    '& .MuiChip-icon': { color: 'inherit !important' },
                  }}
                />
              )}
              <Chip
                icon={user.disabled ? undefined : <VerifiedIcon />}
                label={
                  user.disabled
                    ? t('profile.accountInfo.statusDisabled')
                    : t('profile.accountInfo.statusActive')
                }
                sx={{
                  bgcolor: user.disabled ? 'rgba(239, 68, 68, 0.15)' : 'rgba(34, 197, 94, 0.15)',
                  color: user.disabled ? '#fca5a5' : '#86efac',
                  fontWeight: 600,
                  backdropFilter: 'blur(10px)',
                  border: user.disabled
                    ? '1px solid rgba(239, 68, 68, 0.4)'
                    : '1px solid rgba(34, 197, 94, 0.4)',
                  '& .MuiChip-icon': { color: 'inherit !important' },
                }}
              />
            </Stack>
          </Stack>

          <Box
            sx={{
              bgcolor: alpha(theme.palette.common.black, 0.3), // Fundo escuro para destacar o texto em contraste
              backdropFilter: 'blur(12px)',
              p: 2.5,
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.common.white, 0.15)}`,
              minWidth: 220,
            }}
          >
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <EmailIcon sx={{ color: 'common.white', fontSize: 20 }} />
                <Typography variant="body2" sx={{ color: 'common.white' }} noWrap>
                  {user.email}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <CalendarIcon sx={{ color: 'common.white', fontSize: 20 }} />
                <Typography variant="body2" sx={{ color: 'common.white' }}>
                  {t('profile.accountInfo.createdAt')}: {formatDate(user.createdAt)}
                </Typography>
              </Stack>
            </Stack>
          </Box>
        </Stack>
      </Paper>

      {/* Quick Stats */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4} md={4}>
          <StatCard
            icon={<ShieldIcon sx={{ fontSize: 28 }} />}
            value={user.groups?.length ?? 0}
            label={t('groups.title')}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={4}>
          <StatCard
            icon={<AppsIcon sx={{ fontSize: 28 }} />}
            value={user.applications?.length ?? 0}
            label={t('profile.applications.title')}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={4}>
          <StatCard
            icon={<VerifiedIcon sx={{ fontSize: 28 }} />}
            value={
              user.disabled
                ? t('profile.accountInfo.statusDisabled')
                : t('profile.accountInfo.statusActive')
            }
            label={t('profile.accountInfo.status')}
          />
        </Grid>
      </Grid>

      {/* Layout de duas colunas (Desktop) */}
      <Grid container spacing={3}>
        {/* Coluna da Esquerda: Aplicações e Organização */}
        <Grid item xs={12} md={7} lg={8}>
          <Stack spacing={3}>
            {/* Applications */}
            <Card
              elevation={0}
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                  transform: 'translateY(-4px)',
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                  <SectionIcon icon={<AppsIcon color="primary" />} />
                  <Typography variant="h6" fontWeight={600} sx={{ flex: 1 }}>
                    {t('profile.applications.title')}
                  </Typography>
                  {user.applications && user.applications.length > 0 && (
                    <Chip label={user.applications.length} size="small" color="primary" />
                  )}
                </Stack>

                {user.applications && user.applications.length > 0 ? (
                  <Grid container spacing={2}>
                    {user.applications.map((app) => (
                      <Grid item key={app.id} xs={12} sm={6}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 2,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.04),
                              borderColor: 'primary.main',
                            },
                          }}
                        >
                          <Stack direction="row" alignItems="center" spacing={2}>
                            <AppAvatar name={app.name} />
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body1" fontWeight={600} noWrap>
                                {app.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {app.code}
                              </Typography>
                            </Box>
                          </Stack>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}
                  >
                    <AppsIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="body2" color="text.secondary">
                      {t('profile.applications.noApplications')}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Organization */}
            {(user.dealerId || user.tenantId) && (
              <Card
                elevation={0}
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                    <SectionIcon icon={<BusinessIcon color="primary" />} />
                    <Typography variant="h6" fontWeight={600}>
                      {t('profile.organization.title')}
                    </Typography>
                  </Stack>

                  <Stack spacing={2}>
                    {user.dealerId && (
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          bgcolor: alpha(theme.palette.primary.main, 0.04),
                          border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                        }}
                      >
                        <Stack direction="row" spacing={2} alignItems="center">
                          {user.dealerLogoUrl ? (
                            <Avatar
                              src={user.dealerLogoUrl}
                              variant="rounded"
                              sx={{
                                width: 56,
                                height: 56,
                                boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.1)}`,
                              }}
                            />
                          ) : (
                            <AppAvatar name={t('profile.organization.dealer')} />
                          )}
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                textTransform: 'uppercase',
                                fontWeight: 600,
                                letterSpacing: '0.04em',
                              }}
                            >
                              {t('profile.organization.dealer')}
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                              {user.dealerId}
                            </Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    )}

                    {user.tenantId && (
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          bgcolor: alpha(theme.palette.primary.main, 0.04),
                          border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                        }}
                      >
                        <Stack direction="row" spacing={2} alignItems="center">
                          {user.tenantLogoUrl ? (
                            <Avatar
                              src={user.tenantLogoUrl}
                              variant="rounded"
                              sx={{
                                width: 56,
                                height: 56,
                                boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.1)}`,
                              }}
                            />
                          ) : (
                            <AppAvatar name={t('profile.organization.tenant')} />
                          )}
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                textTransform: 'uppercase',
                                fontWeight: 600,
                                letterSpacing: '0.04em',
                              }}
                            >
                              {t('profile.organization.tenant')}
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                              {user.tenantId}
                            </Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            )}
          </Stack>
        </Grid>

        {/* Coluna da Direita: Segurança */}
        <Grid item xs={12} md={5} lg={4}>
          <Card
            elevation={0}
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                transform: 'translateY(-4px)',
              },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                <SectionIcon icon={<SecurityIcon color="primary" />} />
                <Typography variant="h6" fontWeight={600}>
                  {t('profile.security.title')}
                </Typography>
              </Stack>

              {user.mustChangePassword && (
                <Alert
                  severity="warning"
                  sx={{ mb: 3, '& .MuiAlert-message': { fontSize: '0.8125rem' } }}
                >
                  {t('profile.security.mustChange')}
                </Alert>
              )}

              {/* Formulário ou Resumo da Senha */}
              <Collapse in={showPasswordForm}>
                <Box
                  id="password-change-form"
                  role="region"
                  aria-label={t('profile.updatePassword')}
                >
                  {passwordError && (
                    <Alert
                      severity="error"
                      sx={{ mb: 2, '& .MuiAlert-message': { fontSize: '0.8125rem' } }}
                    >
                      {passwordError}
                    </Alert>
                  )}
                  {passwordSuccess && (
                    <Alert
                      severity="success"
                      sx={{ mb: 2, '& .MuiAlert-message': { fontSize: '0.8125rem' } }}
                    >
                      {t('profile.success.passwordChanged')}
                    </Alert>
                  )}

                  <Box component="form" onSubmit={handleChangePassword}>
                    <Stack spacing={2.5}>
                      <TextField
                        label={t('profile.currentPassword')}
                        type="password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        fullWidth
                        size="small"
                        autoComplete="current-password"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LockIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <TextField
                        label={t('profile.newPassword')}
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        fullWidth
                        size="small"
                        autoComplete="new-password"
                        helperText={t('profile.passwordHelper')}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LockIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                      {newPassword && passwordStrength && (
                        <Box role="status" aria-label={`Força da senha: ${strengthLabel}`}>
                          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              {t('profile.security.passwordStrength.label')}
                            </Typography>
                            <Typography
                              variant="caption"
                              fontWeight={600}
                              sx={{ color: strengthColors[passwordStrength] }}
                            >
                              {strengthLabel}
                            </Typography>
                          </Stack>
                          <LinearProgress
                            variant="determinate"
                            value={strengthValues[passwordStrength]}
                            sx={{
                              height: 6,
                              borderRadius: 3,
                              bgcolor: alpha(strengthColors[passwordStrength], 0.15),
                              '& .MuiLinearProgress-bar': {
                                bgcolor: strengthColors[passwordStrength],
                                borderRadius: 3,
                              },
                            }}
                          />
                        </Box>
                      )}
                      <TextField
                        label={t('profile.confirmPassword')}
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        fullWidth
                        size="small"
                        autoComplete="new-password"
                        error={!!confirmPassword && newPassword !== confirmPassword}
                        helperText={
                          !!confirmPassword && newPassword !== confirmPassword
                            ? t('profile.error.passwordMismatch')
                            : ' '
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LockIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <Stack direction="row" spacing={1.5}>
                        {!user.mustChangePassword && (
                          <CustomButton
                            variant="secondary"
                            fullWidth
                            disabled={isChangingPassword}
                            onClick={() => setShowPasswordForm(false)}
                          >
                            {t('common.cancel')}
                          </CustomButton>
                        )}
                        <CustomButton
                          variant="primary"
                          fullWidth
                          disabled={
                            isChangingPassword ||
                            !currentPassword ||
                            !newPassword ||
                            !confirmPassword ||
                            newPassword !== confirmPassword
                          }
                          type="submit"
                        >
                          {isChangingPassword ? (
                            <CircularProgress size={20} sx={{ color: 'common.white' }} />
                          ) : (
                            t('profile.updatePassword')
                          )}
                        </CustomButton>
                      </Stack>
                    </Stack>
                  </Box>
                </Box>
              </Collapse>

              {!showPasswordForm && (
                <Stack spacing={2} sx={{ mt: 1 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack spacing={0.5}>
                      <Typography variant="caption" color="text.secondary">
                        {t('profile.changePassword')}
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        ••••••••
                      </Typography>
                    </Stack>
                    <CustomButton
                      variant="outline"
                      customSize={{ height: 36 }}
                      icon={<LockIcon />}
                      iconPosition="left"
                      onClick={() => setShowPasswordForm(true)}
                      aria-label={t('profile.security.changePasswordAction')}
                    >
                      {t('profile.security.changePasswordAction')}
                    </CustomButton>
                  </Stack>
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
